/**
 * CounselCore Push Notification Context
 * Provides push notification functionality throughout the app
 * 
 * Created: January 6, 2026
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  initializeFirebase, 
  requestNotificationPermission, 
  onForegroundMessage,
  getNotificationStatus,
  isFirebaseInitialized
} from '../config/firebase';

const PushNotificationContext = createContext(null);

/**
 * Internal provider that uses auth context
 * This allows the provider to work with dependencies from auth
 */
export const PushNotificationProvider = ({ 
  children, 
  token, 
  apiCall, 
  organization, 
  toast 
}) => {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isSupported, setIsSupported] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check notification support and permission on mount
  useEffect(() => {
    const status = getNotificationStatus();
    setIsSupported(status.supported);
    setPermissionStatus(status.permission);
    
    // Check if user previously enabled notifications
    const wasEnabled = localStorage.getItem('counselcore_push_enabled') === 'true';
    if (wasEnabled && status.permission === 'granted') {
      // Auto re-enable if previously enabled and still has permission
      setPushEnabled(true);
    }
  }, []);

  // Listen for foreground messages when enabled
  useEffect(() => {
    if (!pushEnabled || !isFirebaseInitialized()) return;

    const unsubscribe = onForegroundMessage((payload) => {
      // Show toast for foreground notifications
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';
      
      toast?.info?.(title, {
        description: body,
        duration: 5000,
        action: payload.data?.action ? {
          label: 'View',
          onClick: () => handleNotificationAction(payload.data)
        } : undefined
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [pushEnabled, toast]);

  // Handle notification action clicks
  const handleNotificationAction = useCallback((data) => {
    switch (data?.action) {
      case 'view_appointment':
        window.location.href = '/appointments';
        break;
      case 'open_messages':
        window.location.href = '/communications';
        break;
      case 'view_tasks':
        window.location.href = '/tasks';
        break;
      default:
        break;
    }
  }, []);

  // API call helper with token
  const makeApiCall = useCallback(async (endpoint, options = {}) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
    const headers = {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };
    
    try {
      const response = await fetch(url, { ...options, headers });
      return await response.json();
    } catch (err) {
      console.error('API call failed:', err);
      throw err;
    }
  }, [token]);

  // Enable push notifications
  const enablePushNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check if organization has push notifications configured
      const pushConfig = organization?.integration_settings?.push_notifications;
      
      if (!pushConfig?.enabled) {
        throw new Error('Push notifications not enabled for this organization. Contact your administrator.');
      }

      if (!pushConfig?.api_key && !pushConfig?.api_key_encrypted) {
        throw new Error('Push notifications not configured. Please configure Firebase in Integration Settings.');
      }

      // Initialize Firebase with org config
      const messagingInstance = await initializeFirebase({
        apiKey: pushConfig.api_key || pushConfig.api_key_encrypted,
        projectId: pushConfig.project_id,
        senderId: pushConfig.sender_id,
        appId: pushConfig.app_id
      });

      if (!messagingInstance) {
        throw new Error('Failed to initialize push notifications. Your browser may not support this feature.');
      }

      // Request permission and get token
      const result = await requestNotificationPermission(pushConfig.vapid_key);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get notification permission');
      }

      // Register token with backend
      const registerResponse = await makeApiCall('/api/integrations/push/register-device', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_token: result.token,
          device_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language
          }
        })
      });

      if (!registerResponse.success) {
        throw new Error(registerResponse.error || 'Failed to register device');
      }

      // Success!
      setFcmToken(result.token);
      setPushEnabled(true);
      setPermissionStatus('granted');
      localStorage.setItem('counselcore_push_enabled', 'true');
      
      toast?.success?.('Push notifications enabled! You\'ll receive alerts for appointments, messages, and tasks.');
      
      return true;
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
      setError(err.message);
      toast?.error?.(err.message || 'Failed to enable push notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [organization, makeApiCall, toast]);

  // Disable push notifications
  const disablePushNotifications = useCallback(async () => {
    setIsLoading(true);

    try {
      if (fcmToken) {
        await makeApiCall(`/api/integrations/push/unregister-device?device_token=${encodeURIComponent(fcmToken)}`, {
          method: 'DELETE'
        });
      }

      setPushEnabled(false);
      setFcmToken(null);
      localStorage.removeItem('counselcore_push_enabled');
      
      toast?.info?.('Push notifications disabled');
    } catch (err) {
      console.error('Failed to disable push notifications:', err);
      toast?.error?.('Failed to disable push notifications');
    } finally {
      setIsLoading(false);
    }
  }, [fcmToken, makeApiCall, toast]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    if (!pushEnabled) {
      toast?.error?.('Please enable push notifications first');
      return false;
    }

    try {
      const response = await makeApiCall('/api/integrations/push/test', {
        method: 'POST'
      });

      if (response.success) {
        toast?.success?.('Test notification sent! Check your notifications.');
        return true;
      } else {
        toast?.error?.(response.error || 'Failed to send test notification');
        return false;
      }
    } catch (err) {
      console.error('Failed to send test notification:', err);
      toast?.error?.('Failed to send test notification');
      return false;
    }
  }, [pushEnabled, makeApiCall, toast]);

  const value = {
    // State
    pushEnabled,
    fcmToken,
    permissionStatus,
    isSupported,
    isLoading,
    error,
    
    // Actions
    enablePushNotifications,
    disablePushNotifications,
    sendTestNotification,
    
    // Helpers
    canEnableNotifications: isSupported && permissionStatus !== 'denied',
    isBlocked: permissionStatus === 'denied'
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};

// Hook to use push notification context
export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  
  if (!context) {
    // Return a safe fallback if used outside provider
    return {
      pushEnabled: false,
      fcmToken: null,
      permissionStatus: 'default',
      isSupported: false,
      isLoading: false,
      error: null,
      enablePushNotifications: async () => false,
      disablePushNotifications: async () => {},
      sendTestNotification: async () => false,
      canEnableNotifications: false,
      isBlocked: false
    };
  }
  
  return context;
};

export default PushNotificationContext;
